import EditIcon from '@mui/icons-material/Edit';
import EngineeringIcon from '@mui/icons-material/Engineering';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import SettingsIcon from '@mui/icons-material/Settings';
import StoreIcon from '@mui/icons-material/Store';
import {
  Button,
  Typography,
  Box,
  Stack,
  List,
  ListItem,
  Chip,
  Link,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import ColoredAvatar from 'core/components/ColoredAvatar';
import { getFormattedRuPhone, getFullName } from 'core/utils/commonUtils';
import { getOrderActionName } from 'core/utils/orderUtils';
import { getRoleName } from 'core/utils/userUtils';
import useCurrentUser from 'hooks/swr/useCurrentUser';
import CardedPage from 'layout/pages/CardedPage';
import PageHeaderWrapper from 'layout/pages/PageHeaderWrapper';
import { isNil } from 'lodash';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import StoreContext from 'stores/context/StoreContext';
import ProfilePageSkeleton from './ProfilePageSkeleton';
import type { FC } from 'react';

const title = 'Профиль';

const ProfileViewPage: FC = () => {
  const navigate = useNavigate();
  const { data: user, isLoading } = useCurrentUser();
  const { shopStore } = useContext(StoreContext);

  const handleEditClick = (): void => {
    navigate('edit');
  };

  if (isLoading || isNil(user)) {
    return <Box p={3}>Загрузка...</Box>;
  }

  const Header: FC = () => (
    <PageHeaderWrapper>
      <Stack direction={'row'} alignItems='center' gap={2}>
        <ColoredAvatar
          alt={user.surname}
          sx={{
            width: { xs: undefined, sm: 80 },
            height: { xs: undefined, sm: 80 },
            fontSize: { xs: undefined, sm: '2rem' },
          }}>
          {user.surname[0]}
        </ColoredAvatar>
        <Box>
          <Typography variant='h5'>{getFullName(user)}</Typography>
          <Typography color='textSecondary'>{getFormattedRuPhone(user.phone)}</Typography>
        </Box>
      </Stack>
      <Button variant='contained' onClick={handleEditClick} startIcon={<EditIcon />}>
        Редактировать
      </Button>
    </PageHeaderWrapper>
  );

  if (isLoading || isNil(user)) {
    return (
      <CardedPage title={title}>
        <ProfilePageSkeleton />
      </CardedPage>
    );
  }

  return (
    <CardedPage title='Профиль' header={<Header />}>
      <List>
        <ListItem>
          <ListItemIcon>
            <PersonIcon />
          </ListItemIcon>
          <ListItemText primary='Роль' secondary={getRoleName(user.role)} />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            <PhoneIcon />
          </ListItemIcon>
          <ListItemText
            primary='Телефон'
            secondary={
              <Link href={`tel:+${user.phone}`}>{getFormattedRuPhone(user.phone)}</Link>
            }
          />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            <EngineeringIcon />
          </ListItemIcon>
          <ListItemText
            primary='Работает на этапах'
            secondaryTypographyProps={{ component: 'div' }}
            secondary={
              <Stack direction='row' flexWrap={'wrap'} gap={1}>
                {user.actionAccesses.map((a) => (
                  <Chip key={a} label={getOrderActionName(a)} />
                ))}
              </Stack>
            }
          />
        </ListItem>
      </List>

      {shopStore.shop && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant='h6'>Информация об организации</Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <StoreIcon />
              </ListItemIcon>
              <ListItemText primary='Название' secondary={shopStore.shop.name} />
            </ListItem>
            {shopStore.shop.description && (
              <ListItem>
                <ListItemIcon>
                  <StoreIcon />
                </ListItemIcon>
                <ListItemText primary='Описание' secondary={shopStore.shop.description} />
              </ListItem>
            )}
            {shopStore.shop.settings && (
              <ListItem>
                <ListItemIcon>
                  <SettingsIcon />
                </ListItemIcon>
                <ListItemText
                  primary='Настройки'
                  secondary={
                    <Stack direction='column' gap={1}>
                      <Typography variant='body2'>
                        Показывать заказы без даты этапа в календаре:{' '}
                        {shopStore.shop.settings.showOrdersWithoutActionDateInCalendar
                          ? 'Да'
                          : 'Нет'}
                      </Typography>
                    </Stack>
                  }
                />
              </ListItem>
            )}
          </List>
        </>
      )}
    </CardedPage>
  );
};

export default ProfileViewPage;
